import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  Link,
  Person,
  CheckCircle,
  Mail,
} from '@mui/icons-material';
import {
  childManagementService,
  type ChildSearchMinimalResult,
} from '../services/childManagementService';
import { authService } from '../services/authService';

interface ChildLinkingFormProps {
  onLinkChild: (childId: number) => Promise<void>;
  onCancel?: () => void;
  title?: string;
}

const ChildLinkingForm: React.FC<ChildLinkingFormProps> = ({
  onLinkChild,
  onCancel,
  title = 'Add Registered Child',
}) => {
  const [searchEmail, setSearchEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchResult, setSearchResult] = useState<ChildSearchMinimalResult | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [relationshipId, setRelationshipId] = useState<number | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [codePhase, setCodePhase] = useState(false);

  const handleSearch = async () => {
    if (!searchEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    setSearching(true);
    setError(null);
    setSuccess(null);
    setSearchResult(null);
    setCodePhase(false);
    setRelationshipId(null);
    setVerificationCode('');

    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser || !authService.isAuthenticated()) {
        setError('You must be logged in to search for children. Please log in again.');
        return;
      }

      const result = await childManagementService.searchChildByEmail(searchEmail);
      setSearchResult(result);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again to continue.');
        authService.logout();
      } else {
        setError(err.message || 'Failed to search for child');
      }
    } finally {
      setSearching(false);
    }
  };

  const handleRequestLink = async () => {
    if (!searchResult) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('Parent not authenticated');
      }

      const result = await childManagementService.requestChildLink(
        searchResult.id,
        currentUser.id
      );

      if (result.alreadyLinked) {
        setSuccess(result.message);
        setShowConfirmDialog(false);
        await onLinkChild(searchResult.id);
        return;
      }

      setRelationshipId(result.relationshipId);
      setSuccess(result.message);
      setCodePhase(true);
      setShowConfirmDialog(false);
    } catch (err: any) {
      setError(err.message || 'Failed to request link');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!relationshipId || !searchResult) return;

    if (!/^\d{6}$/.test(verificationCode.trim())) {
      setError('Please enter the 6-digit code from the student\'s email.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await childManagementService.verifyLinkCode(relationshipId, verificationCode.trim());
      setSuccess('Child linked successfully!');
      await onLinkChild(searchResult.id);
    } catch (err: any) {
      setError(err.message || 'Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!relationshipId) return;

    setLoading(true);
    setError(null);

    try {
      await childManagementService.resendLinkCode(relationshipId);
      setSuccess('A new verification code has been sent to the student\'s email.');
      setVerificationCode('');
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Search by the child&apos;s full email address. For privacy, only their initials are shown.
        The student must share the verification code sent to their email.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {!codePhase && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <Search sx={{ mr: 1 }} />
              Search for Child
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Child's Email Address"
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="student@example.com"
                disabled={searching}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={searching || !searchEmail.trim()}
                startIcon={searching ? <CircularProgress size={20} /> : <Search />}
                sx={{ minWidth: 120, alignSelf: { xs: 'stretch', sm: 'center' } }}
              >
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {searchResult && !codePhase && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <Person sx={{ mr: 1 }} />
              Matching Student
            </Typography>

            <Typography variant="body2" color="text.secondary">Initials</Typography>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, letterSpacing: 2 }}>
              {searchResult.initials}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              If these initials match your child, confirm to send a verification code to their email.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={() => setSearchResult(null)}>Search again</Button>
              <Button variant="contained" onClick={() => setShowConfirmDialog(true)} startIcon={<Link />}>
                Send verification code
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {codePhase && relationshipId && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <Mail sx={{ mr: 1 }} />
              Enter Verification Code
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ask your child for the 6-digit code from their StudyStride email, then enter it below.
            </Typography>

            <TextField
              fullWidth
              label="6-digit code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
              sx={{ mb: 2 }}
              disabled={loading}
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
              <Button onClick={handleResendCode} disabled={loading}>
                Resend code
              </Button>
              <Button
                variant="contained"
                onClick={handleVerifyCode}
                disabled={loading || verificationCode.length !== 6}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
              >
                {loading ? 'Verifying...' : 'Complete link'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {onCancel && (
        <>
          <Divider sx={{ my: 3 }} />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onCancel}>Cancel</Button>
          </Stack>
        </>
      )}

      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>Confirm link request</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Send a verification code to the student account matching <strong>{searchEmail}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The student ({searchResult?.initials}) will receive an email with a 6-digit code.
            Only you can enter that code to complete the link.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button
            onClick={handleRequestLink}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Mail />}
          >
            {loading ? 'Sending...' : 'Send code'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChildLinkingForm;
